/**
 * ChefVoiceSettings - Settings panel for Chef voice features
 * 
 * Provides controls for privacy, phrase hints, transcript management, and engine selection
 */

import React, { useState, useEffect } from 'react';
import { Modal, Heading, Text } from './ui';
import { probe, getProbeStats, probeAllEngines, getEngineStatus } from '../services/speech';
import VADSettings from './VADSettings';

const ChefVoiceSettings = ({ 
    speechManager,
    isOpen, 
    onClose,
    onSettingsChange,
    // Additional props for system status
    currentEngine,
    engineStatuses,
    micOn,
    statusMessage,
    triggerDetected,
    onEngineSwitch
}) => {
    const [settings, setSettings] = useState({
        // Privacy settings
        requireConfirmation: true,
        autoDeleteTranscripts: true,
        maxTranscripts: 100,
        
        // Engine settings
        preferredEngine: 'smart',
        enablePhraseHints: true,
        enableTranscripts: true,
        enablePushToTalk: true,
        
        // VAD settings
        energyThreshold: 0.01,
        silenceThreshold: 0.005,
        silenceDuration: 1000,
        minSpeechDuration: 200
    });

    const [showVADSettings, setShowVADSettings] = useState(false);
    const [engineStats, setEngineStats] = useState(null);
    const [transcriptStats, setTranscriptStats] = useState(null);
    
    const [localEngineStatuses, setLocalEngineStatuses] = useState({});
    const [isProbing, setIsProbing] = useState(false);

    // Load engine statuses from probe cache
    useEffect(() => {
        if (isOpen) {
            const statuses = {};
            ['browser', 'vad', 'streaming'].forEach(engine => {
                statuses[engine] = getEngineStatus(engine);
            });
            setLocalEngineStatuses(statuses);
        }
    }, [isOpen]);

    // Load settings from speech manager
    useEffect(() => {
        if (speechManager && isOpen) {
            const browserInfo = speechManager.getBrowserInfo();
            const stats = speechManager.getEngineStats();
            
            setEngineStats(stats);
            
            if (speechManager.getTranscriptStats) {
                setTranscriptStats(speechManager.getTranscriptStats());
            }
            
            // Load current settings
            setSettings(prev => ({
                ...prev,
                preferredEngine: browserInfo.currentEngine || 'smart',
                enablePhraseHints: browserInfo.phraseHintsEnabled !== false,
                enableTranscripts: browserInfo.transcriptsEnabled !== false,
                enablePushToTalk: browserInfo.pushToTalkEnabled !== false
            }));
        }
    }, [speechManager, isOpen]);

    const handleSettingChange = (key, value) => {
        const newSettings = { ...settings, [key]: value };
        setSettings(newSettings);
        
        if (onSettingsChange) {
            onSettingsChange(newSettings);
        }
    };

    const handleVADThresholdsChange = (thresholds) => {
        if (speechManager && speechManager.updateVADThresholds) {
            speechManager.updateVADThresholds(thresholds);
        }
        // Forward to active VAD if available
        try {
            const engine = speechManager.getEngine && speechManager.getEngine();
            if (engine && engine.vad && engine.vad.updateParams) {
                engine.vad.updateParams({
                    silenceMs: thresholds.silenceDuration,
                    minSpeechMs: thresholds.minSpeechDuration,
                    threshold: thresholds.energyThreshold,
                    silenceThreshold: thresholds.silenceThreshold
                });
            }
        } catch (_) {}
        
        setSettings(prev => ({
            ...prev,
            ...thresholds
        }));
    };

    const handleEngineSwitch = async (engineType) => {
        const status = (engineStatuses || {})[engineType];
        if (status && status.status !== 'available') {
            console.warn(`Cannot switch to ${engineType}: ${status.reason}`);
            return;
        }
        
        if (speechManager && speechManager.switchEngine) {
            const success = await speechManager.switchEngine(engineType);
            if (success) {
                setSettings(prev => ({ ...prev, preferredEngine: engineType }));
            }
        }
    };

    const handleRefreshEngines = async () => {
        setIsProbing(true);
        try {
            await probeAllEngines();
            // Update statuses after probe completes
            const statuses = {};
            ['browser', 'vad', 'streaming'].forEach(engine => {
                statuses[engine] = getEngineStatus(engine);
            });
            setLocalEngineStatuses(statuses);
        } catch (error) {
            console.error('Failed to refresh engines:', error);
        } finally {
            setIsProbing(false);
        }
    };

    const handleExportTranscripts = () => {
        if (speechManager && speechManager.exportTranscripts) {
            const data = speechManager.exportTranscripts({ format: 'json' });
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `chef-transcripts-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
    };

    const handleClearTranscripts = () => {
        if (speechManager && speechManager.clearTranscripts) {
            if (window.confirm('Are you sure you want to clear all transcripts? This cannot be undone.')) {
                speechManager.clearTranscripts();
                setTranscriptStats(speechManager.getTranscriptStats());
            }
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose}>
                <div className="max-w-4xl mx-auto">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <Heading level={2} style={{ color: '#1b263b' }}>
                                Chef Voice Settings
                            </Heading>
                            <Text className="text-neutral-900 opacity-60 mt-1">
                                Configure voice recognition, privacy, and transcript management
                            </Text>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-neutral-900 hover:opacity-60 transition-opacity duration-120 font-mono text-lg border-none bg-transparent p-0 m-0"
                        >
                            ✕
                        </button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left Column */}
                        <div className="space-y-6">
                            {/* System Status */}
                            <div className="bg-neutral-50 hairline p-4">
                                <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">
                                    System Status
                                </Text>
                                
                                {/* Current Status */}
                                <div className="mb-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <Text className="text-sm font-medium text-neutral-900">Status</Text>
                                        <div className={`px-3 py-1 rounded text-xs font-mono uppercase tracking-wider ${
                                            micOn ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                                        }`}>
                                            {micOn ? 'Active' : 'Inactive'}
                                        </div>
                                    </div>
                                    <Text className="text-sm text-neutral-900 opacity-80">
                                        {statusMessage}
                                    </Text>
                                </div>

                                {/* Microphone Status */}
                                <div className="mb-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <Text className="text-sm font-medium text-neutral-900">Microphone</Text>
                                        <div className="flex items-center">
                                            <div className={`w-2 h-2 rounded-full mr-2 ${
                                                micOn ? 'bg-red-500 animate-pulse' : 'bg-gray-300'
                                            }`} />
                                            <Text className="text-xs font-mono uppercase tracking-wider">
                                                {micOn ? 'ON' : 'OFF'}
                                            </Text>
                                        </div>
                                    </div>
                                </div>

                                {/* Current Engine */}
                                <div className="mb-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <Text className="text-sm font-medium text-neutral-900">Current Engine</Text>
                                        <Text className="text-xs font-mono uppercase tracking-wider bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                            {currentEngine}
                                        </Text>
                                    </div>
                                </div>

                                {/* Trigger Detection */}
                                {triggerDetected && (
                                    <div className="p-3 bg-green-50 border border-green-200 rounded">
                                        <Text className="text-sm font-medium text-green-800">
                                            🎯 Trigger phrase detected!
                                        </Text>
                                    </div>
                                )}
                            </div>

                            {/* Engine Availability */}
                            <div className="bg-neutral-50 hairline p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900">
                                        Engine Availability
                                    </Text>
                                    <button
                                        onClick={handleRefreshEngines}
                                        disabled={isProbing}
                                        className="px-2 py-1 text-xs font-mono uppercase tracking-wider bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isProbing ? 'Testing...' : 'Refresh'}
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {Object.entries(engineStatuses || {}).map(([engine, status]) => (
                                        <div key={engine} className="flex items-center justify-between">
                                            <Text className="text-sm text-neutral-900 capitalize">{engine}</Text>
                                            <div className={`px-2 py-1 rounded text-xs font-mono uppercase tracking-wider ${
                                                status.available ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                                {status.available ? 'Available' : 'Unavailable'}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Engine Selection */}
                            <div className="bg-neutral-50 hairline p-4">
                                <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">
                                    Speech Engine
                                </Text>
                                
                                {engineStats && (
                                    <div className="space-y-2">
                                        <Text className="text-sm text-neutral-900">
                                            <strong>Current:</strong> {engineStats.currentEngine}
                                        </Text>
                                        <Text className="text-sm text-neutral-900">
                                            <strong>Available:</strong> {engineStats.availableEngines} engines
                                        </Text>
                                        {engineStats.fallbackEnabled && (
                                            <Text className="text-sm text-neutral-900">
                                                <strong>Fallback:</strong> Enabled
                                            </Text>
                                        )}
                                    </div>
                                )}
                                
                                <div className="flex gap-2 mt-3">
                                    {['streaming', 'vad', 'browser'].map(engine => {
                                        const status = (engineStatuses || {})[engine];
                                        const isAvailable = status && status.status === 'available';
                                        const isTesting = status && status.status === 'testing';
                                        const isSelected = settings.preferredEngine === engine;
                                        
                                        return (
                                            <button
                                                key={engine}
                                                onClick={() => isAvailable ? handleEngineSwitch(engine) : null}
                                                disabled={!isAvailable || isTesting}
                                                className={`px-3 py-1 text-xs font-mono uppercase tracking-wider transition-colors ${
                                                    isSelected
                                                        ? 'bg-neutral-900 text-neutral-0'
                                                        : isAvailable
                                                            ? 'bg-neutral-200 text-neutral-900 hover:bg-neutral-300'
                                                            : 'bg-neutral-100 text-neutral-500 cursor-not-allowed'
                                                }`}
                                                title={!isAvailable ? `${engine}: ${status?.reason || 'Not available'}` : ''}
                                            >
                                                {engine}
                                                {isTesting && ' (testing...)'}
                                                {!isAvailable && !isTesting && ' (unavailable)'}
                                            </button>
                                        );
                                    })}
                                </div>
                                
                                {/* Refresh Engines Button */}
                                <div className="mt-3">
                                    <button
                                        onClick={handleRefreshEngines}
                                        disabled={isProbing}
                                        className="px-3 py-1 text-xs font-mono uppercase tracking-wider bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {isProbing ? 'Testing...' : 'Refresh Engines'}
                                    </button>
                                </div>
                            </div>

                            {/* Privacy Settings */}
                            <div className="bg-neutral-50 hairline p-4">
                                <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">
                                    Privacy & Security
                                </Text>
                                
                                <div className="space-y-3">
                                    <label className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            checked={settings.requireConfirmation}
                                            onChange={(e) => handleSettingChange('requireConfirmation', e.target.checked)}
                                            className="checkbox-label"
                                        />
                                        <Text className="text-sm text-neutral-900">
                                            Require confirmation before saving transcripts
                                        </Text>
                                    </label>
                                    
                                    <label className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            checked={settings.autoDeleteTranscripts}
                                            onChange={(e) => handleSettingChange('autoDeleteTranscripts', e.target.checked)}
                                            className="checkbox-label"
                                        />
                                        <Text className="text-sm text-neutral-900">
                                            Auto-delete transcripts after 24 hours
                                        </Text>
                                    </label>
                                    
                                    <div className="flex items-center space-x-3">
                                        <Text className="text-sm text-neutral-900">
                                            Max transcripts:
                                        </Text>
                                        <input
                                            type="number"
                                            min="10"
                                            max="1000"
                                            value={settings.maxTranscripts}
                                            onChange={(e) => handleSettingChange('maxTranscripts', parseInt(e.target.value))}
                                            className="w-20 px-2 py-1 text-sm border border-neutral-300"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Feature Toggles */}
                            <div className="bg-neutral-50 hairline p-4">
                                <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">
                                    Features
                                </Text>
                                
                                <div className="space-y-3">
                                    <label className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            checked={settings.enablePhraseHints}
                                            onChange={(e) => handleSettingChange('enablePhraseHints', e.target.checked)}
                                            className="checkbox-label"
                                        />
                                        <Text className="text-sm text-neutral-900">
                                            Enable recipe-specific phrase hints
                                        </Text>
                                    </label>
                                    
                                    <label className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            checked={settings.enableTranscripts}
                                            onChange={(e) => handleSettingChange('enableTranscripts', e.target.checked)}
                                            className="checkbox-label"
                                        />
                                        <Text className="text-sm text-neutral-900">
                                            Save voice transcripts with timestamps
                                        </Text>
                                    </label>
                                    
                                    <label className="flex items-center space-x-3">
                                        <input
                                            type="checkbox"
                                            checked={settings.enablePushToTalk}
                                            onChange={(e) => handleSettingChange('enablePushToTalk', e.target.checked)}
                                            className="checkbox-label"
                                        />
                                        <Text className="text-sm text-neutral-900">
                                            Enable push-to-talk mode
                                        </Text>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="space-y-6">
                            {/* VAD Settings */}
                            <div className="bg-neutral-50 hairline p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900">
                                        Voice Activity Detection
                                    </Text>
                                    <button
                                        onClick={() => setShowVADSettings(true)}
                                        className="px-3 py-1 text-xs font-mono uppercase tracking-wider bg-neutral-200 text-neutral-900 hover:bg-neutral-300 transition-colors"
                                    >
                                        Configure
                                    </button>
                                </div>
                                
                                <div className="space-y-2 text-sm text-neutral-900">
                                    <div>Energy: {settings.energyThreshold}</div>
                                    <div>Silence: {settings.silenceThreshold}</div>
                                    <div>Duration: {settings.silenceDuration}ms</div>
                                </div>
                            </div>

                            {/* Transcript Statistics */}
                            {transcriptStats && (
                                <div className="bg-neutral-50 hairline p-4">
                                    <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">
                                        Transcript Statistics
                                    </Text>
                                    
                                    <div className="space-y-2 text-sm text-neutral-900">
                                        <div><strong>Total:</strong> {transcriptStats.total}</div>
                                        <div><strong>Pending:</strong> {transcriptStats.pending}</div>
                                        <div><strong>Confirmed:</strong> {transcriptStats.confirmed}</div>
                                        <div><strong>Last 24h:</strong> {transcriptStats.last24h}</div>
                                        <div><strong>Last 7d:</strong> {transcriptStats.last7d}</div>
                                    </div>
                                    
                                    <div className="flex gap-2 mt-3">
                                        <button
                                            onClick={handleExportTranscripts}
                                            className="px-3 py-1 text-xs font-mono uppercase tracking-wider bg-neutral-200 text-neutral-900 hover:bg-neutral-300 transition-colors"
                                        >
                                            Export
                                        </button>
                                        <button
                                            onClick={handleClearTranscripts}
                                            className="px-3 py-1 text-xs font-mono uppercase tracking-wider bg-neutral-200 text-neutral-900 hover:bg-neutral-300 transition-colors"
                                        >
                                            Clear All
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Engine History */}
                            {engineStats && engineStats.engineHistory && engineStats.engineHistory.length > 0 && (
                                <div className="bg-neutral-50 hairline p-4">
                                    <Text className="font-mono text-sm uppercase tracking-wider text-neutral-900 mb-3">
                                        Engine History
                                    </Text>
                                    
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                        {engineStats.engineHistory.slice(0, 5).map((entry, index) => (
                                            <div key={index} className="text-xs text-neutral-900">
                                                <div className="font-mono">{entry.engine}</div>
                                                <div className="opacity-60">
                                                    {new Date(entry.timestamp).toLocaleString()}
                                                </div>
                                                {entry.reason && (
                                                    <div className="opacity-60">{entry.reason}</div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end mt-8">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-neutral-900 text-neutral-0 hover:opacity-60 transition-opacity font-mono text-sm uppercase tracking-wider"
                        >
                            Close Settings
                        </button>
                    </div>
                </div>
            </Modal>

            {/* VAD Settings Modal */}
            <VADSettings
                isOpen={showVADSettings}
                onClose={() => setShowVADSettings(false)}
                onThresholdsChange={handleVADThresholdsChange}
                initialThresholds={{
                    energyThreshold: settings.energyThreshold,
                    silenceThreshold: settings.silenceThreshold,
                    silenceDuration: settings.silenceDuration,
                    minSpeechDuration: settings.minSpeechDuration
                }}
            />
        </>
    );
};

export default ChefVoiceSettings;
